import { useEffect, useRef, useState } from 'react';
import { Mic, Send, Square } from 'lucide-react';
import { MAX_DURATION_MS, saveVoiceNote } from '@core/firebase/voiceNotes';
import { useLanguage } from '@/contexts/LanguageContext';
import { CardTitle, Notice, pill } from '@/components/PageKit';
import { cn } from '@/lib/cn';

/**
 * A teacher recording a short note for a guardian — م-3-04 and م-3-05.
 *
 * The requirement is not only "record": it is record, LISTEN BACK, and
 * record again before sending. So the take is held here until it is sent,
 * and sending is a separate press — a note about somebody's child should
 * not leave on the same press that stopped the microphone.
 *
 * The half-minute limit stops the recorder itself rather than warning
 * afterwards. The server enforces it again, and in bytes, because a
 * duration is only what a client says.
 */

/** What browsers actually produce; the callable accepts each of them. */
const PREFERRED = ['audio/mp4', 'audio/webm'];

function pickType(): string {
  const supported = (window as { MediaRecorder?: { isTypeSupported?: (t: string) => boolean } })
    .MediaRecorder?.isTypeSupported;
  if (!supported) return PREFERRED[0];
  return PREFERRED.find((t) => supported(t)) ?? PREFERRED[0];
}

/**
 * The take, as base64 for the callable.
 *
 * FileReader rather than blob.arrayBuffer(): the latter is missing in jsdom
 * and was missing from Safari for years, and this is the one step between a
 * recording existing and it reaching the guardian. A base64 data URL is what
 * every browser has always been able to produce.
 */
function toBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('unreadable-take'));
    reader.onload = () => {
      const url = String(reader.result ?? '');
      const comma = url.indexOf(',');
      resolve(comma === -1 ? '' : url.slice(comma + 1));
    };
    reader.readAsDataURL(blob);
  });
}

export function VoiceNoteRecorder({
  studentId, studentName, sessionId,
}: {
  studentId: string;
  studentName: string | null;
  sessionId?: string | null;
}) {
  const { t } = useLanguage();
  const [recording, setRecording] = useState(false);
  const [take, setTake] = useState<{ blob: Blob; url: string; durationMs: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const startedRef = useRef(0);
  const stopperRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** A take left behind is a recording of a child still in this tab. */
  const dropTake = () => {
    setTake((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  useEffect(() => () => {
    if (stopperRef.current) clearTimeout(stopperRef.current);
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      recorderRef.current.stop();
    }
    dropTake();
  }, []);

  // A note is about ONE pupil's turn. When the turn moves on, an unsent take
  // would otherwise be sent about the next child.
  useEffect(() => { dropTake(); setSent(false); }, [studentId]);

  const stop = () => {
    if (stopperRef.current) { clearTimeout(stopperRef.current); stopperRef.current = null; }
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
    setRecording(false);
  };

  const start = async () => {
    if (busy || recording) return;
    setErrorKey(null);
    setSent(false);
    dropTake();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickType();
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      chunksRef.current = [];
      startedRef.current = Date.now();

      recorder.ondataavailable = (e: BlobEvent) => {
        if (e.data && e.data.size) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        /*
         * The recorder ending is what ends the recording — not the button
         * that asked it to. A browser can stop one on its own (the stream
         * ends, the tab is throttled), and a UI that only clears this flag in
         * its own handler then shows "stop" forever with a finished take
         * behind it and no way to send.
         */
        setRecording(false);
        // The microphone is released the moment the take ends: a tab holding
        // an open microphone is a tab still listening.
        stream.getTracks().forEach((track) => track.stop());
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const durationMs = Math.min(MAX_DURATION_MS, Date.now() - startedRef.current);
        setTake({ blob, url: URL.createObjectURL(blob), durationMs });
      };

      recorder.start();
      setRecording(true);
      // م-3-05 on the device: the recorder STOPS at the limit rather than
      // letting somebody record two minutes and be refused afterwards.
      stopperRef.current = setTimeout(stop, MAX_DURATION_MS);
    } catch {
      setErrorKey('voiceNotes.micDenied');
    }
  };

  const send = async () => {
    if (!take || busy) return;
    setBusy(true);
    setErrorKey(null);
    try {
      const audio = await toBase64(take.blob);
      await saveVoiceNote({
        studentId,
        audio,
        contentType: take.blob.type || 'audio/mp4',
        durationMs: take.durationMs,
        sessionId: sessionId ?? null,
      });
      dropTake();
      setSent(true);
    } catch (e) {
      const message = String((e as { message?: string })?.message ?? '');
      setErrorKey(
        message.includes('audio-too-long') || message.includes('audio-too-large')
          ? 'voiceNotes.tooLong'
          : 'voiceNotes.sendFailed',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="note-recorder" className="card-soft space-y-4 p-5">
      <CardTitle icon={<Mic />} level={3}>{t('voiceNotes.recordTitle')}</CardTitle>
      <p className="text-sm text-helper">
        {studentName ? t('voiceNotes.recordFor', { name: studentName }) : null}
        <span className="ms-2">{t('voiceNotes.limitHint')}</span>
      </p>

      {errorKey && <Notice tone="danger" testId="recorder-error">{t(errorKey)}</Notice>}
      {sent && <Notice tone="ok" testId="note-sent">{t('voiceNotes.sent')}</Notice>}

      {/* م-3-04: heard back before it is sent, and re-recordable until then. */}
      {take && (
        <audio
          data-testid="take-preview"
          src={take.url}
          controls
          controlsList="nodownload"
          className="w-full"
        />
      )}

      <div className="flex flex-wrap items-center gap-2">
        {recording ? (
          <button
            type="button"
            data-testid="stop-recording"
            onClick={stop}
            className={cn(pill.base, pill.md, pill.danger)}
          >
            <Square className="h-4 w-4" />
            {t('voiceNotes.stopRecording')}
          </button>
        ) : (
          <button
            type="button"
            data-testid="start-recording"
            onClick={() => { void start(); }}
            disabled={busy}
            // With a take in hand, sending is the main action and recording
            // again the quiet one.
            className={cn(pill.base, pill.md, take ? pill.quiet : pill.primary)}
          >
            <Mic className="h-4 w-4" />
            {take ? t('voiceNotes.rerecord') : t('voiceNotes.record')}
          </button>
        )}

        {take && !recording && (
          <button
            type="button"
            data-testid="send-note"
            onClick={() => { void send(); }}
            disabled={busy}
            aria-busy={busy}
            className={cn(pill.base, pill.md, pill.primary)}
          >
            <Send className="h-4 w-4 rtl:-scale-x-100" />
            {t('voiceNotes.send')}
          </button>
        )}

        {recording && (
          <span data-testid="recording-now" className="flex items-center gap-2 text-sm text-danger">
            <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-danger" />
            {t('voiceNotes.recording')}
          </span>
        )}
      </div>
    </section>
  );
}
